import { Component, signal, computed, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AuthService } from '../../../features/auth/auth.service';
import { BackofficeAccessService } from './sidebar.service';

interface SidebarItem {
  label: string;
  icon?: string;
  route?: string;
  moduleKey?: string;
  parentModuleKey?: string;
  children?: SidebarItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatSidenavModule, MatButtonModule],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss'],
})
export class Sidebar implements OnInit, AfterViewInit {
  @ViewChild('drawer', { static: true }) sidenav!: MatSidenav; // usa el template reference explícito
  isMobile = signal(false);
  activeGroups = signal<Record<string, boolean>>({});
  userFullName = signal<string | null>(localStorage.getItem('user_full_name'));
  roles = signal<string[]>(
    (() => {
      const raw = localStorage.getItem('roles');
      try {
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    })(),
  );

  constructor(
    private authService: AuthService,
    private router: Router,
    private backofficeAccessService: BackofficeAccessService,
    breakpoint: BreakpointObserver,
  ) {
    breakpoint.observe(['(max-width: 959px)']).subscribe((result) => {
      const mobile = result.matches;
      this.isMobile.set(mobile);

      // avoid layout jump: only act if sidenav already initialized
      if (this.sidenav) {
        if (mobile) {
          this.sidenav.close();
        } else {
          this.sidenav.open();
        }
      }
    });
    window.addEventListener('storage', () => {
      this.userFullName.set(localStorage.getItem('user_full_name'));
      const raw = localStorage.getItem('roles');
      try {
        this.roles.set(raw ? JSON.parse(raw) : []);
      } catch {
        this.roles.set([]);
      }
    });
  }

  ngOnInit() {
    // initialize sidenav BEFORE first paint to avoid layout shift
    if (this.isMobile()) {
      this.sidenav.close();
    } else {
      this.sidenav.open();
    }

    this.backofficeAccessService.loadAccess().subscribe();
  }

  menu: SidebarItem[] = [
    {
      label: 'Principal',
      children: [{ label: 'Inicio', icon: 'dashboard', route: '/inicio' }],
    },
    {
      label: 'Gestión de Usuarios',
      children: [
        {
          label: 'Usuarios',
          icon: 'group',
          route: '/usuarios',
          moduleKey: 'admin_users',
        },
        {
          label: 'Pacientes',
          icon: 'person',
          route: '/pacientes',
          moduleKey: 'patients',
        },
        { label: 'Grupo Familiar', icon: 'diversity_3', route: '/familia', moduleKey: 'family' },
        { label: 'Vínculos Familiares', icon: 'link', route: '/vinculos', moduleKey: 'family' },
        {
          label: 'Validación de identidad',
          icon: 'verified_user',
          route: '/validaciones',
          moduleKey: 'identity_validations',
        },
      ],
    },
    {
      label: 'Gestión de Contenido',
      children: [
        { label: 'Categorías', icon: 'category', route: '/categoria', moduleKey: 'content' },
        { label: 'Preguntas Frecuentes', icon: 'help', route: '/faqs', moduleKey: 'content' },
      ],
    },
    {
      label: 'Gestión Comercial',
      children: [
        {
          label: 'Planes',
          icon: 'inventory_2',
          route: '/planes',
          moduleKey: 'plans',
          parentModuleKey: 'commercial',
        },
        {
          label: 'Planes Empresariales',
          icon: 'corporate_fare',
          route: '/planes-empresariales',
          moduleKey: 'plans',
          parentModuleKey: 'commercial',
        },
        {
          label: 'Suscripciones',
          icon: 'autorenew',
          route: '/suscripciones',
          moduleKey: 'subscriptions',
          parentModuleKey: 'commercial',
        },
        {
          label: 'Medios de Pago',
          icon: 'credit_card',
          route: '/metodos-pago',
          moduleKey: 'payment_methods',
          parentModuleKey: 'commercial_payments',
        },
        {
          label: 'Pagos',
          icon: 'payments',
          route: '/pagos',
          moduleKey: 'payments',
          parentModuleKey: 'commercial',
        },
      ],
    },
    {
      label: 'Registro de Actividad',
      children: [
        {
          label: 'Botón de Emergencia',
          icon: 'warning',
          route: '/boton-panico',
          moduleKey: 'emergency',
        },
      ],
    },
    {
      label: 'Configuración de Sistema',
      children: [
        {
          label: 'Notificaciones',
          icon: 'notifications',
          route: '/notificaciones',
          moduleKey: 'notifications',
          parentModuleKey: 'notifications_system',
        },
        {
          label: 'Reportes',
          icon: 'bar_chart',
          route: '/reportes',
          moduleKey: 'reports',
          parentModuleKey: 'reports_system',
        },
        {
          label: 'Config. Botón de Emergencia',
          icon: 'settings',
          route: '/configuracion-boton-panico',
          moduleKey: 'emergency',
          parentModuleKey: 'settings_system',
        },
        {
          label: 'Configuración',
          icon: 'settings',
          route: '/roles',
          moduleKey: 'settings',
          parentModuleKey: 'settings_system',
        },
        {
          label: 'Roles y Permisos',
          icon: 'admin_panel_settings',
          route: '/roles',
          moduleKey: 'roles_permissions',
        },
        { label: 'Soporte', icon: 'support_agent', route: '/soporte', moduleKey: 'support_system' },
      ],
    },
    {
      label: 'Legales',
      children: [
        { label: 'Términos y Condiciones', icon: 'description', route: '/tyc', moduleKey: 'legal' },
        {
          label: 'Política de Privacidad',
          icon: 'privacy_tip',
          route: '/privacidad',
          moduleKey: 'legal',
        },
      ],
    },
    {
      label: 'Cerrar sesión',
      icon: 'logout',
    },
  ];

  toggleGroup(label: string) {
    this.activeGroups.update((v) => ({ ...v, [label]: !v[label] }));
  }

  isOpen(label: string) {
    return !!this.activeGroups()[label];
  }

  toggleSidebar() {
    if (!this.sidenav) return;
    this.sidenav.toggle();
  }

  ngAfterViewInit() {
    // no-op: handled earlier to avoid flicker
  }

  navigate(item: SidebarItem) {
    if (this.isDisabled(item)) {
      return;
    }

    if (item.label === 'Cerrar sesión') {
      this.logout();
      return;
    }

    if (item.route) {
      this.router.navigate([item.route]);
      if (this.isMobile()) this.sidenav.close();
    }
  }

  isVisible(item: SidebarItem): boolean {
    // items públicos/sin permisos.
    if (!item.moduleKey) {
      return true;
    }

    // si backend ya envía el módulo específico,
    // usamos exclusivamente ese valor.
    if (this.backofficeAccessService.hasModuleKey(item.moduleKey)) {
      return this.backofficeAccessService.hasModule(item.moduleKey);
    }

    // fallback temporal para módulos legacy/macros.
    if (item.parentModuleKey) {
      return this.backofficeAccessService.hasModule(item.parentModuleKey);
    }

    // si no existe en access => ocultar.
    return false;
  }

  isDisabled(item: SidebarItem): boolean {
    if (!item.moduleKey) {
      return false;
    }

    // si existe explícitamente y está en false.
    if (this.backofficeAccessService.hasModuleKey(item.moduleKey)) {
      return !this.backofficeAccessService.hasModule(item.moduleKey);
    }

    // si depende de un parent legacy.
    if (item.parentModuleKey) {
      return !this.backofficeAccessService.hasModule(item.parentModuleKey);
    }

    // no existe en access.
    return true;
  }

  hasVisibleChildren(item: SidebarItem): boolean {
    if (!item.children?.length) return false;

    return item.children.some((child) => this.isVisible(child));
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  roleLabel = computed(() => {
    const role = this.roles()[0];
    return role ? role.toUpperCase() : null;
  });
}
